import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PRODUCT_CATEGORIES } from "../data/site";

const MAIN_PAGES = [
  { name: "Home",           path: "/",              desc: "Brand overview, product highlights, global presence & lead form." },
  { name: "Our Craft",      path: "/our-craft",     desc: "The story of hand block printing, from block carving to finished textile." },
  { name: "Products",       path: "/products",      desc: "All 11 categories — browse the full range." },
  { name: "Custom Orders",  path: "/custom-orders", desc: "Three design paths: library, exclusive, and client-supplied artwork." },
  { name: "Projects",       path: "/projects",      desc: "Case studies — hospitality, retail, and private-label partnerships." },
  { name: "About",          path: "/about",         desc: "Brand heritage, workshop, founder story, and sustainability." },
  { name: "Contact",        path: "/contact",       desc: "Request a quote, book a discovery call, or send a direct inquiry." },
];

const LEGAL = [
  { name: "Terms & Conditions", path: "/terms" },
  { name: "Privacy Policy",     path: "/privacy" },
];

export default function Sitemap() {
  return (
    <>
      <Helmet>
        <title>Sitemap | Indus Art</title>
        <meta name="description" content="All pages on Indus Art — product categories, brand story, contact, and more." />
        <link rel="canonical" href="https://www.indusartjaipur.com/sitemap" />
      </Helmet>

      <main className="pt-32 pb-24 bg-[#faf6ef]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="mb-12">
            <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-3">Navigation</div>
            <h1 className="text-4xl md:text-6xl font-light text-[#1e2a5a] leading-[1.1]">Sitemap</h1>
          </div>

          {/* Main pages */}
          <section className="mb-14">
            <h2 className="text-xs font-bold tracking-[0.25em] uppercase text-[#1a1a1a]/40 mb-5">Main Pages</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {MAIN_PAGES.map((p, i) => (
                <motion.div
                  key={p.path}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }}
                >
                  <Link
                    to={p.path}
                    className="group flex flex-col gap-1 bg-white rounded-2xl p-5 border border-[#1a1a1a]/8 hover:shadow-soft hover:border-[#1e2a5a]/20 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-[#1e2a5a] text-sm">{p.name}</span>
                      <ArrowRight size={14} className="text-[#b4462a] group-hover:translate-x-1 transition" />
                    </div>
                    <span className="text-xs text-[#1a1a1a]/55 leading-[1.5]">{p.desc}</span>
                    <span className="text-[10px] text-[#1a1a1a]/30 font-mono mt-1">indusartjaipur.com{p.path}</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Product categories */}
          <section className="mb-14">
            <h2 className="text-xs font-bold tracking-[0.25em] uppercase text-[#1a1a1a]/40 mb-5">
              Product Categories <span className="ml-2 text-[#b4462a]">{PRODUCT_CATEGORIES.length} categories</span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {PRODUCT_CATEGORIES.map((c, i) => (
                <motion.div
                  key={c.key}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }}
                >
                  <Link
                    to={`/products/${c.slug}`}
                    className="group flex flex-col gap-1 bg-white rounded-2xl p-5 border border-[#1a1a1a]/8 hover:shadow-soft hover:border-[#1e2a5a]/20 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-[#1e2a5a] text-sm">{c.name}</span>
                      <ArrowRight size={14} className="text-[#b4462a] group-hover:translate-x-1 transition" />
                    </div>
                    <span className="text-xs text-[#1a1a1a]/55 leading-[1.5]">{c.tagline}</span>
                    {c.subcategories && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.subcategories.map((s) => (
                          <span key={s} className="text-[9px] bg-[#faf6ef] border border-[#1a1a1a]/10 text-[#1a1a1a]/50 rounded-full px-2 py-0.5">{s}</span>
                        ))}
                      </div>
                    )}
                    <span className="text-[10px] text-[#1a1a1a]/30 font-mono mt-1">/products/{c.slug}</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Legal */}
          <section>
            <h2 className="text-xs font-bold tracking-[0.25em] uppercase text-[#1a1a1a]/40 mb-5">Legal</h2>
            <div className="flex flex-wrap gap-4">
              {LEGAL.map((p) => (
                <Link
                  key={p.path}
                  to={p.path}
                  className="text-sm font-semibold text-[#1e2a5a] hover:text-[#b4462a] underline underline-offset-4 transition"
                >
                  {p.name}
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
